import { Alert, Box, MenuItem, TextField, Typography } from "@mui/material";

import FieldLabel from "components/common/FieldLabel";
import UserAvatar from "components/common/UserAvatar";
import type { SecurePerson } from "features/secureUploads/secureUploads";

import { COLLECTION_NAME_MAX } from "./CollectionDialogs";
import { gainingLine, losingLine } from "./moveModel";

export const ROOT = "root";
export const NEW = "new";

function People({ people }: { people: SecurePerson[] }) {
  return (
    <Box sx={{ display: "grid", gap: 0.75, mt: 1 }}>
      {people.map((person) => (
        <Box
          key={person.id}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <UserAvatar person={person} size={22} />
          <Typography variant="body2">{person.name}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/** The project to move into (left out inside one project) and its collection,
 * a new one included. */
export function MoveTargetFields({
  projects,
  projectChoice,
  onProject,
  projectId,
  collections,
  collectionChoice,
  onCollection,
  newName,
  onNewName,
  disabled,
}: {
  /** The open projects to choose from; null when the project is fixed. */
  projects: { id: number; name: string }[] | null;
  projectChoice: string;
  onProject: (value: string) => void;
  projectId: number | null;
  collections: { id: number; name: string }[];
  collectionChoice: string;
  onCollection: (value: string) => void;
  newName: string;
  onNewName: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <>
      {projects ? (
        <Box>
          <FieldLabel htmlFor="move-project" required>
            Project
          </FieldLabel>
          <TextField
            select
            fullWidth
            size="small"
            id="move-project"
            inputProps={{ "aria-label": "Project" }}
            value={projectChoice}
            disabled={disabled}
            SelectProps={{ displayEmpty: true }}
            onChange={(event) => onProject(event.target.value)}
          >
            <MenuItem value="" disabled>
              Choose an open project
            </MenuItem>
            {projects.map((project) => (
              <MenuItem key={project.id} value={String(project.id)}>
                {project.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      ) : null}
      {projectId ? (
        <Box>
          <FieldLabel htmlFor="move-collection">Collection</FieldLabel>
          <TextField
            select
            fullWidth
            size="small"
            id="move-collection"
            inputProps={{ "aria-label": "Collection" }}
            value={collectionChoice}
            disabled={disabled}
            onChange={(event) => onCollection(event.target.value)}
          >
            <MenuItem value={ROOT}>No collection</MenuItem>
            {collections.map((collection) => (
              <MenuItem key={collection.id} value={String(collection.id)}>
                {collection.name}
              </MenuItem>
            ))}
            <MenuItem value={NEW}>+ New collection</MenuItem>
          </TextField>
          {collectionChoice === NEW ? (
            <TextField
              fullWidth
              size="small"
              autoFocus
              sx={{ mt: 1.5 }}
              placeholder="Name the new collection"
              value={newName}
              disabled={disabled}
              onChange={(event) => onNewName(event.target.value)}
              inputProps={{
                maxLength: COLLECTION_NAME_MAX,
                "aria-label": "New collection name",
              }}
            />
          ) : null}
        </Box>
      ) : null}
    </>
  );
}

/** Who a move would leave out, and who it would show the files to. */
export function MoveImpactAlerts({
  losing,
  gaining,
  projectName,
  files = 1,
}: {
  losing: SecurePerson[];
  gaining: SecurePerson[];
  projectName: string;
  files?: number;
}) {
  return (
    <>
      {losing.length > 0 ? (
        <Alert severity="warning">
          {losingLine(losing, projectName, files)}
          <People people={losing} />
        </Alert>
      ) : null}
      {gaining.length > 0 ? (
        <Alert severity="info">
          {gainingLine(gaining, files)}
          <People people={gaining} />
        </Alert>
      ) : null}
    </>
  );
}
